import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

interface Option {
  id: string;
  name: string;
}

interface MultiSelectDropdownProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  showSearch?: boolean; // New prop to show/hide search input
  onSelectedOptionsChange?: (options: Option[]) => void;
}

/**
 * Multi-select dropdown component with chips showing selected items
 */
const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  selectedIds,
  onChange,
  options,
  placeholder = "Select options",
  className = "",
  loading = false,
  disabled = false,
  showSearch = true, // Default to true for backward compatibility
  onSelectedOptionsChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelectedIds, setTempSelectedIds] = useState<string[]>(selectedIds);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const displayTextRef = useRef<HTMLSpanElement>(null);
  const measureTextRef = useRef<HTMLSpanElement>(null);

  const selectedOptions = useMemo(
    () => options.filter(option => selectedIds.includes(option.id)),
    [options, selectedIds]
  );
  const filteredOptions = showSearch
    ? options.filter(option => option.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : options; // When search is disabled, show all options

  // Calculate display text with smart truncation
  const [displayText, setDisplayText] = useState<string>('');
  const [overflowCount, setOverflowCount] = useState<number>(0);

  // Immediate fallback display text (used while calculating)
  const fallbackDisplayText = useMemo(() => {
    if (selectedOptions.length === 0) return '';
    if (selectedOptions.length === 1) return selectedOptions[0].name;
    // Show first item + count as fallback
    return selectedOptions[0].name;
  }, [selectedOptions]);

  const fallbackOverflowCount = useMemo(() => {
    if (selectedOptions.length <= 1) return 0;
    return selectedOptions.length - 1;
  }, [selectedOptions]);

  useEffect(() => {
    // Set immediate fallback
    if (selectedOptions.length === 0) {
      setDisplayText('');
      setOverflowCount(0);
      return;
    }

    if (selectedOptions.length === 1) {
      setDisplayText(selectedOptions[0].name);
      setOverflowCount(0);
      return;
    }

    // Set fallback immediately while calculating
    setDisplayText(fallbackDisplayText);
    setOverflowCount(fallbackOverflowCount);

    // Calculate what fits in the available space
    const calculateDisplay = () => {
      if (!containerRef.current) {
        // Keep fallback
        return;
      }

      const containerWidth = containerRef.current.offsetWidth;
      
      // If container width is 0, it means it's not rendered yet, try again later
      if (containerWidth === 0) {
        setTimeout(calculateDisplay, 50);
        return;
      }
      
      const availableWidth = containerWidth - 80; // Account for padding (px-3 = 12px), chevron (pr-8 = 32px), and some margin (~36px)
      
      const allNames = selectedOptions.map(opt => opt.name);
      let fittingNames: string[] = [];
      
      // Always show at least the first item
      fittingNames.push(allNames[0]);
      
      // Measure each additional name and see how many fit
      for (let i = 1; i < allNames.length; i++) {
        const name = allNames[i];
        const testText = fittingNames.join(', ') + ', ' + name;
        
        // Use canvas to measure text width accurately
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (context) {
          // Match the actual font used in the component (text-sm = 14px)
          context.font = '14px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
          const textWidth = context.measureText(testText).width;
          
          // Also account for "+N" indicator if not the last item
          const remainingCount = allNames.length - i - 1;
          const indicatorText = remainingCount > 0 ? ` +${remainingCount}` : '';
          const indicatorWidth = remainingCount > 0 ? context.measureText(indicatorText).width : 0;
          
          if (textWidth + indicatorWidth <= availableWidth) {
            fittingNames.push(name);
          } else {
            break;
          }
        } else {
          // Fallback if canvas not available - show first 2 items max
          if (fittingNames.length < 2) {
            fittingNames.push(name);
          }
          break;
        }
      }
      
      const remaining = selectedOptions.length - fittingNames.length;
      setDisplayText(fittingNames.join(', '));
      setOverflowCount(remaining);
    };

    // Calculate on mount and resize with a small delay to ensure DOM is ready
    let resizeTimeout: ReturnType<typeof setTimeout>;
    
    const scheduleCalculate = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateDisplay, 10);
    };
    
    // Initial calculation
    scheduleCalculate();
    
    // Also try after a longer delay to ensure layout is complete
    const initialTimeout = setTimeout(calculateDisplay, 100);
    
    const resizeObserver = new ResizeObserver(() => {
      scheduleCalculate();
    });
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      if (initialTimeout) clearTimeout(initialTimeout);
      resizeObserver.disconnect();
    };
  }, [selectedOptions.length, selectedOptions.map(o => o.name).join(',')]); // Recalculate when selected options change

  // Sync tempSelectedIds when selectedIds prop changes (e.g., from outside)
  // Only sync when dropdown is closed to avoid resetting user selections
  useEffect(() => {
    if (!isOpen) {
      // Compare array contents to prevent infinite loops when array reference changes but content is the same
      setTempSelectedIds((prevTemp) => {
        const prevSorted = [...prevTemp].sort().join(',');
        const selectedSorted = [...selectedIds].sort().join(',');
        
        if (prevSorted !== selectedSorted) {
          return selectedIds;
        }
        return prevTemp; // No change needed, return previous value to prevent re-render
      });
    }
  }, [selectedIds, isOpen]);

  // Notify parent of selected options changes
  // Only notify when selectedIds actually changes, not when options or callback changes
  useEffect(() => {
    if (onSelectedOptionsChange) {
      const tempSelectedOptions = options.filter(option => selectedIds.includes(option.id));
      onSelectedOptionsChange(tempSelectedOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds]); // Only depend on selectedIds to prevent infinite loops

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4, // 4px gap (mt-1)
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        // Also check if click is outside the container
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          // Reset temp selections if closing without applying
          setTempSelectedIds((prevTemp) => {
            const prevSorted = [...prevTemp].sort().join(',');
            const selectedSorted = [...selectedIds].sort().join(',');
            
            if (prevSorted !== selectedSorted) {
              return selectedIds;
            }
            return prevTemp; // No change needed, return previous value to prevent re-render
          });
          setIsOpen(false);
          setSearchTerm('');
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, selectedIds]);

  const handleToggleOption = (optionId: string) => {
    if (disabled) return;
    
    setTempSelectedIds((prev) => {
      if (prev.includes(optionId)) {
        return prev.filter(id => id !== optionId);
      } else {
        return [...prev, optionId];
      }
    });
  };

  const handleApply = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(tempSelectedIds);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Use functional update to avoid dependency on selectedIds reference
    setTempSelectedIds(() => selectedIds);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSelectAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Select all filtered options (respecting search filter)
    const allFilteredIds = filteredOptions.map(option => option.id);
    setTempSelectedIds(allFilteredIds);
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setTempSelectedIds([]);
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <div
          ref={containerRef}
          className={`w-full min-h-[38px] text-sm border rounded-lg px-3 py-2 pr-8 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all flex items-center ${
            disabled
              ? 'bg-gray-100 cursor-not-allowed border-gray-200'
              : isOpen
              ? 'bg-white border-blue-500 cursor-text shadow-sm'
              : 'bg-white border-gray-300 cursor-text hover:border-gray-400'
          }`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          {selectedOptions.length === 0 ? (
            <span className="text-gray-400">{placeholder}</span>
          ) : (
            <span 
              ref={displayTextRef}
              className="text-gray-700 truncate block min-w-0 flex-1"
              title={selectedOptions.map(opt => opt.name).join(', ')}
            >
              {displayText || fallbackDisplayText}
              {(overflowCount > 0 || (displayText === '' && fallbackOverflowCount > 0)) && (
                <span className="text-gray-500 ml-1 whitespace-nowrap">
                  +{displayText ? overflowCount : fallbackOverflowCount}
                </span>
              )}
            </span>
          )}
          {/* Hidden span for measuring text width */}
          <span
            ref={measureTextRef}
            className="invisible absolute whitespace-nowrap text-sm"
            style={{ visibility: 'hidden', position: 'absolute', top: '-9999px' }}
          >
            {displayText}
          </span>
        </div>
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg
            className="w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {isOpen && !disabled && dropdownPosition && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed z-[9999] min-w-[200px] bg-white border border-gray-300 rounded-lg shadow-xl"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
          }}
        >
          {(showSearch || filteredOptions.length > 0) && (
            <div className={`p-2 ${showSearch ? 'border-b border-gray-200' : ''} bg-gray-50`}>
              {showSearch && (
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchTerm(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  placeholder="Search..."
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {/* Select All / Clear All buttons */}
              {filteredOptions.length > 0 && (
                <div className={`flex items-center justify-between ${showSearch ? 'mt-2 pt-2 border-t border-gray-200' : ''}`}>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={handleClearAll}
                    className="text-xs text-gray-600 hover:text-gray-800 font-medium px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                  >
                    Clear All
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="overflow-y-auto p-1" style={{ maxHeight: '200px' }}>
            {loading ? (
              <div className="p-2 text-xs text-gray-500 text-center">Loading...</div>
            ) : filteredOptions.length === 0 ? (
              <div className="p-2 text-xs text-gray-500 text-center">No options found</div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = tempSelectedIds.includes(option.id);
                return (
                  <label
                    key={option.id}
                    htmlFor={`checkbox-${option.id}`}
                    onClick={(e) => {
                      // Prevent default label behavior - we handle toggle manually via onChange
                      if ((e.target as HTMLElement).tagName !== 'INPUT') {
                        e.preventDefault();
                        // Manually trigger toggle when clicking label text
                        handleToggleOption(option.id);
                      }
                    }}
                    className={`px-3 py-2 text-sm cursor-pointer flex items-center space-x-2 rounded-md hover:bg-blue-50 transition-colors ${
                      isSelected ? 'bg-blue-50' : ''
                    }`}
                  >
                    <input
                      id={`checkbox-${option.id}`}
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggleOption(option.id);
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                    />
                    <span className={isSelected ? 'font-medium text-blue-900' : 'text-gray-700'}>{option.name}</span>
                  </label>
                );
              })
            )}
          </div>
          {/* Apply/Cancel buttons */}
          <div className="flex items-center justify-end gap-2 p-2 border-t border-gray-200 bg-gray-50 min-w-0">
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors whitespace-nowrap flex-shrink-0"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors whitespace-nowrap flex-shrink-0"
            >
              Apply
            </button>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default MultiSelectDropdown;

